import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import {
  useMediaQuery,
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  Edit, 
  Save, 
  Cancel,
  Person, 
  Phone, 
  ExpandMore, 
  Business, 
  Badge, 
  AccountBalance, 
  FamilyRestroom, 
  School,
  ArrowBack,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { parseUTCDate } from 'state/helpers';
import useAuthCheck from 'hooks/useAuthCheck';
import ScrollToTop from 'components/ScrollToTop';
import FlexCenter from 'components/FlexCenter';
import FlexBetween from 'components/FlexBetween';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import CrewPersonalInfo from './CrewPersonalInfo';
import CrewContact from './CrewContact';
import CrewEmployment from './CrewEmployment';
import CrewDocuments from './CrewDocuments';
import CrewPayroll from './CrewPayroll';
import CrewFamily from './CrewFamily';
import CrewQualifications from './CrewQualifications';


const schema = yup.object({
  crewCode: yup.string().required('Crew code is required'),
  crewName: yup.string().required('Crew name is required'),
});

const buildInitialValues = (cm) => ({
  crewCode: cm?.crewCode || '',
  crewName: cm?.crewName || '',
  shortName: cm?.shortName || '',
  gender: cm?.gender || 'FM',
  birthday: cm?.birthday || null,
  placeOfBirth: cm?.placeOfBirth || '',
  placeOfOrigin: cm?.placeOfOrigin || '',
  marital: cm?.marital || '',
  location: cm?.location || '',
  nationality: cm?.nationality || '',
  language1: cm?.language1 || '',
  language2: cm?.language2 || '',
  // Contact
  address: cm?.address || '',
  city: cm?.city || '',
  state: cm?.state || '',
  zip: cm?.zip || '',
  email: cm?.email || '',
  password: '',
  contactCell: cm?.contactCell || '',
  contactCell2: cm?.contactCell2 || '',
  contactCell3: cm?.contactCell3 || '',
  contactTel: cm?.contactTel || '',
  contactTel2: cm?.contactTel2 || '',
  contactTel3: cm?.contactTel3 || '',
  contactCoMob: cm?.contactCoMob || '',
  contactCoMob2: cm?.contactCoMob2 || '',
  contactCoMob3: cm?.contactCoMob3 || '',
  // Employment
  rank: cm?.rank || '',
  department: cm?.department || '',
  division: cm?.division || '',
  deptUnit: cm?.deptUnit || '',
  agency: cm?.agency || '',
  employmentDate: cm?.employmentDate || null,
  employmentContract: cm?.employmentContract || '',
  employmentStartDate: cm?.employmentStartDate || null,
  employmentEndDate: cm?.employmentEndDate || null,
  jobGradeCode: cm?.jobGradeCode || '',
  salaryGradeCode: cm?.salaryGradeCode || '',
  isActive: cm?.isActive ?? true,
  jobTitles: cm?.jobTitles || [],
  // Documents
  licenceNum: cm?.licenceNum || '',
  licenceIssueDate: cm?.licenceIssueDate || null,
  licenceExpiryDate: cm?.licenceExpiryDate || null,
  passportType: cm?.passportType || 'P',
  passportName: cm?.passportName || '',
  firstName: cm?.firstName || '',
  middleName: cm?.middleName || '',
  surname: cm?.surname || '',
  passportNumber: cm?.passportNumber || '',
  passportIssueDate: cm?.passportIssueDate || null,
  passportExpiryDate: cm?.passportExpiryDate || null,
  passportCountry: cm?.passportCountry || '',
  nationalIdNumber: cm?.nationalIdNumber || '',
  nationalIdIssueDate: cm?.nationalIdIssueDate || null,
  nationalIdIssuePlace: cm?.nationalIdIssuePlace || '',
  taxCode: cm?.taxCode || '',
  // Payroll
  baseSalary: cm?.baseSalary || 0,
  bankAccount: cm?.bankAccount || { bankName: '', accountNumber: '' },
  contributionObject: cm?.contributionObject || '',
  isNetIncome: cm?.isNetIncome ?? false,
  insuranceDayCalculationType: Array.isArray(cm?.insuranceDayCalculationType)
    ? (cm.insuranceDayCalculationType[0] || '')
    : (cm?.insuranceDayCalculationType || ''),
  // Family
  nextKinName: cm?.nextKinName || '',
  nextKinRelsn: cm?.nextKinRelsn || '',
  nextKinContact: cm?.nextKinContact || '',
  dependents: cm?.dependents || [],
  // Qualifications
  quals: cm?.quals || '',
  trainingQuals: cm?.trainingQuals || [],
  crewQuals: cm?.crewQuals || [],
});

const SECTIONS = [
  { key: 'personal',       icon: <Person fontSize="small" />,          label: 'Personal Information' },
  { key: 'contact',        icon: <Phone fontSize="small" />,           label: 'Contact' },
  { key: 'family',         icon: <FamilyRestroom fontSize="small" />,  label: 'Family & Dependents' },
  { key: 'employment',     icon: <Business fontSize="small" />,        label: 'Employment' },
  { key: 'payroll',        icon: <AccountBalance fontSize="small" />,  label: 'Payroll' },
  { key: 'documents',      icon: <Badge fontSize="small" />,           label: 'Documents' },
  { key: 'qualifications', icon: <School fontSize="small" />,          label: 'Qualifications' },
];

const DATE_FIELDS = [
  'birthday', 'employmentDate',
  'passportIssueDate', 'passportExpiryDate',
  'nationalIdIssueDate',
  'employmentStartDate', 'employmentEndDate',
];


const CrewMember = () => {
  useAuthCheck();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.user);
  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);

  const isMobile = useMediaQuery('(max-width:600px)');
  const scrollRef = useRef(null);
  const formikRef = useRef(null);

  const isNew = !id || id === 'new';
  const canEdit = isNew || hasRight(permissionMap, 'crewmembers', 'update', false);

  const [crewMember, setCrewMember] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [isEditMode, setIsEditMode] = useState(isNew);
  const [expanded, setExpanded] = useState('personal');

  const fetchCrewMember = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/crewmembers/get/${id}`);
      setCrewMember(data);
    } catch {
      toast.error('Failed to load crew member.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { fetchCrewMember(); }, [fetchCrewMember]);

  const handleAccordion = (panel) => (_, isExpanded) =>
    setExpanded(isExpanded ? panel : false);

  const handleBack = () =>
    navigate('/crewmembers', { state: { filters: location.state?.filters } });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const updatedJobTitles = values.jobTitles.map(jt => ({
        ...jt,
        effectiveFrom: jt.effectiveFrom ? parseUTCDate(jt.effectiveFrom) : '',
        effectiveTo: jt.effectiveTo ? parseUTCDate(jt.effectiveTo) : '',
      }));

      const payload = {
        ...values,
        ...DATE_FIELDS.reduce((acc, field) => {
          acc[field] = values[field] ? parseUTCDate(values[field]) : null;
          return acc;
        }, {}),
        jobTitles: updatedJobTitles,
        insuranceDayCalculationType: values.insuranceDayCalculationType
          ? [values.insuranceDayCalculationType]
          : [],
      };

      if (isNew) {
        await api.post('/crewmembers/new', payload);
        toast.success('Crew member created.');
        resetForm();
        handleBack();
      } else {
        await api.put(`/crewmembers/put/${id}`, payload);
        toast.success('Crew member updated.');
        await fetchCrewMember();
        setIsEditMode(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    }
  };

  if (loading) {
    return (
      <FlexCenter height={LAYOUT_HEIGHT}>
        <CircularProgress />
      </FlexCenter>
    );
  }

  const initialValues = buildInitialValues(crewMember);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="main-container-box">
        <FlexBetween>
          <Header
            title="Crew Member"
            subtitle={isNew ? 'Create' : 'Update'}
          />
          <Stack direction="row" gap={1}>
            {isEditMode ? (
              <>
                <Tooltip title="Save" arrow>
                  <StyledButton onClick={() => formikRef.current?.submitForm()}>
                    <Save />
                  </StyledButton>
                </Tooltip>
                {!isNew && (
                  <Tooltip title="Cancel" arrow>
                    <StyledButton onClick={() => { setIsEditMode(false); formikRef.current?.resetForm(); }}>
                      <Cancel />
                    </StyledButton>
                  </Tooltip>
                )}
              </>
            ) : (
              canEdit && (
                <Tooltip title="Edit" arrow>
                  <StyledButton onClick={() => setIsEditMode(true)}>
                    <Edit />
                  </StyledButton>
                </Tooltip>
              )
            )}
            <Tooltip title="Back" arrow>
              <StyledButton onClick={handleBack}>
                <ArrowBack />
              </StyledButton>
            </Tooltip>
          </Stack>
        </FlexBetween>

        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2}
        >
          <Box 
            ref={scrollRef} 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: 'span 12', overflowY: 'auto' }}
          >
            <Formik
              innerRef={formikRef}
              enableReinitialize
              initialValues={initialValues}
              validationSchema={schema}
              onSubmit={handleSubmit}
            >
              {(formik) => {
                const { values, handleChange, handleBlur, errors, touched, setFieldValue } = formik;

                const renderHeader = () => (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: 2, p: 2,
                    bgcolor: 'background.paper',
                    border: 1, borderColor: 'divider', borderRadius: 2, mb: 2,
                  }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: 'primary.main',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}
                    >
                      {(values.crewName || '?')
                        .trim()
                        .split(' ')
                        .slice(-2)
                        .map(word => word.charAt(0).toUpperCase())
                        .join('')
                      }
                    </Avatar>

                    <Box flex={1} minWidth={0}>
                      <Stack 
                        direction="row" 
                        flexWrap="wrap"
                        alignItems="center" 
                        gap={1} 
                      >
                        <Typography variant="h5" fontWeight="bold" noWrap={!isMobile}>
                          {values.crewName || (isNew ? 'New Crew Member' : '-')}
                        </Typography>
                        {values.crewCode && (
                          <Chip label={values.crewCode} size="small" variant="outlined" />
                        )}                        
                        {crewMember && (
                          <Chip
                            label={crewMember.isActive ? 'Active' : 'Inactive'}
                            color={crewMember.isActive ? 'success' : 'error'}
                            size="small"
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {[
                          values.rank, 
                          values.location ? `${values.location}` : null,
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>

                  </Box>
                );

                const sectionProps = { values, handleChange, handleBlur, errors, touched, setFieldValue, isEditMode };

                return (
                  <>
                    {renderHeader()}

                    {SECTIONS.map((section) => (
                      <Accordion
                        key={section.key}
                        expanded={expanded === section.key}
                        onChange={handleAccordion(section.key)}
                        sx={{ mb: 0.5, '&:before': { display: 'none' }, borderRadius: '8px !important' }}
                        disableGutters
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack 
                            direction="row" 
                            alignItems="center" 
                            gap={1}
                          >
                            <Box sx={{ display: 'flex', color: 'primary.main' }}>
                              {section.icon}
                            </Box>
                            <Typography variant="subtitle2" fontWeight="600">
                              {section.label}
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                          <Divider sx={{ mb: 2 }} />
                          {section.key === 'personal'       && <CrewPersonalInfo {...sectionProps} />}
                          {section.key === 'contact'        && <CrewContact {...sectionProps} />}
                          {section.key === 'employment'     && <CrewEmployment {...sectionProps} />}
                          {section.key === 'documents'      && <CrewDocuments {...sectionProps} />}
                          {section.key === 'payroll'        && <CrewPayroll {...sectionProps} />}
                          {section.key === 'family'         && <CrewFamily {...sectionProps} />}
                          {section.key === 'qualifications' && <CrewQualifications {...sectionProps} />}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </>
                );
              }}
            </Formik>

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default CrewMember;
